import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { Box, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import { ChartData } from 'src/types/analytics';

interface AnalyticsChartProps {
  title: string;
  data: ChartData;
  type: 'line' | 'bar' | 'pie';
  height?: number;
  loading?: boolean;
  options?: {
    indexAxis?: 'x' | 'y';
    plugins?: {
      legend?: {
        display?: boolean;
      };
      tooltip?: {
        mode?: 'index' | 'point';
        intersect?: boolean;
      };
    };
    scales?: {
      x?: {
        grid?: {
          color?: string;
        };
      };
      y?: {
        grid?: {
          display?: boolean;
        };
      };
    };
  };
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  title,
  data,
  type,
  height = 300,
  loading = false,
  options = {}
}) => {
  if (loading) {
    return (
      <Card>
        <CardHeader title={title} />
        <CardContent sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.labels || !data.datasets || data.labels.length === 0) {
    return (
      <Card>
        <CardHeader title={title} />
        <CardContent sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Convert the data format to what Recharts expects
  const chartData = data.labels.map((label, index) => {
    const dataPoint: any = {
      name: typeof data.legendLabels?.[index] === 'object'
        ? (data.legendLabels[index] as { text: string }).text
        : label
    };
    data.datasets.forEach((dataset) => {
      dataPoint[dataset.label] = dataset.data[index];
    });
    return dataPoint;
  });

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            {data.datasets.map((dataset, index) => (
              <Line
                key={dataset.label}
                type="monotone"
                dataKey={dataset.label}
                stroke={typeof dataset.borderColor === 'string' ? dataset.borderColor : COLORS[index % COLORS.length]}
                activeDot={{ r: 8 }}
              />
            ))}
          </LineChart>
        );
      case 'bar':
        const isHorizontal = options.indexAxis === 'y';
        return (
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            height={isHorizontal ? chartData.length * 40 + 100 : height}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={!isHorizontal}
              vertical={isHorizontal}
            />
            <XAxis
              type={isHorizontal ? 'number' : 'category'}
              dataKey={isHorizontal ? undefined : 'name'}
            />
            <YAxis
              type={isHorizontal ? 'category' : 'number'}
              dataKey={isHorizontal ? 'name' : undefined}
              width={isHorizontal ? 200 : 60}
              tickFormatter={isHorizontal ? (value: string) => value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : undefined}
            />
            <Tooltip
              formatter={(value: number, name: string, props: any) => {
                const label = props.payload.name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                return [`${value} changes`, label];
              }}
            />
            {!options.plugins?.legend?.display && <Legend />}
            {data.datasets.map((dataset, index) => (
              <Bar
                key={dataset.label}
                name={dataset.label}
                dataKey={dataset.label}
                fill={typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : COLORS[index % COLORS.length]}
                background={isHorizontal ? { fill: '#eee' } : undefined}
              />
            ))}
          </BarChart>
        );
      case 'pie':
        // Create custom data for the pie chart that uses legendLabels for the Legend if available
        const pieData = data.datasets[0].data.map((value, index) => {
          const legendLabel = data.legendLabels?.[index];
          return {
            name: data.labels[index],
            legendName: typeof legendLabel === 'object' ? legendLabel.text : (legendLabel || data.labels[index]),
            value,
            color: Array.isArray(data.datasets[0].backgroundColor)
              ? data.datasets[0].backgroundColor[index]
              : COLORS[index % COLORS.length]
          };
        });

        // Return just the chart components without wrapping in another Card
        return (
          <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Chart takes 70% of the height */}
            <Box sx={{ height: '70%', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    innerRadius={0}
                    paddingAngle={0}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={true}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color as string}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {/* Legend takes the remaining 30% */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              height: '30%'
            }}>
              {pieData.map((entry, index) => (
                <Box
                  key={`legend-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mx: 0.5,
                    my: 0.25
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      bgcolor: entry.color as string,
                      mr: 0.5
                    }}
                  />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: 'white' }}
                  >
                    {entry.legendName}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent sx={{ height, p: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default AnalyticsChart; 